import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, ChevronDown, ChevronUp, Calendar, User, MapPin, Search, Star, AlertTriangle, Truck, ClipboardCheck, MessageSquare, Gift, Bell, Download, FileText } from 'lucide-react';
type OrderStatus = 'pending-vendor' | 'accepted' | 'pre-inspect' | 'inspection' | 'arrived' | 'in-progress' | 'pending-review' | 'completed' | 'closed' | 'rejected' | 'rework';
type Order = {
  id: number;
  service: string;
  date: string;
  status: OrderStatus;
  vendor: string;
  vendorPhone?: string;
  vendorRating?: number;
  amount: string;
  details: {
    description: string;
    address: string;
    technician: string;
    completedDate?: string;
    scheduledDate?: string;
    warranty: string;
    notes: string;
  };
  statusHistory?: {
    status: string;
    timestamp: string;
    note?: string;
  }[];
  canReview?: boolean;
  reviewDeadline?: string;
  rejectionReason?: string;
};
export function Orders() {
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  // Mock data with new order flow
  const orders: Order[] = [{
    id: 1,
    service: 'Painting Service - Living Room',
    date: 'April 10, 2023',
    status: 'completed',
    vendor: 'ABC Painting Co.',
    vendorPhone: '+60 12-345-6789',
    vendorRating: 4.8,
    amount: 'RM 850.00',
    details: {
      description: 'Complete repainting of living room walls and ceiling with Green Home 0 VOC paint.',
      address: '123 Jalan Bukit Bintang, Kuala Lumpur',
      technician: 'Ahmad Bin Abdullah',
      completedDate: 'April 12, 2023',
      warranty: 'Valid until April 12, 2025',
      notes: 'Customer requested eggshell finish on all walls.'
    },
    statusHistory: [{
      status: 'Order Created',
      timestamp: 'April 10, 2023 09:00 AM'
    }, {
      status: 'Vendor Assigned',
      timestamp: 'April 10, 2023 09:15 AM',
      note: 'ABC Painting Co. assigned based on location'
    }, {
      status: 'Vendor Confirmed',
      timestamp: 'April 10, 2023 10:30 AM'
    }, {
      status: 'Pre-Inspection Scheduled',
      timestamp: 'April 10, 2023 02:00 PM'
    }, {
      status: 'Inspection Completed',
      timestamp: 'April 11, 2023 10:00 AM'
    }, {
      status: 'Work Started',
      timestamp: 'April 12, 2023 08:00 AM'
    }, {
      status: 'Work Completed',
      timestamp: 'April 12, 2023 05:00 PM'
    }],
    canReview: true,
    reviewDeadline: 'April 15, 2023'
  }, {
    id: 2,
    service: 'Waterproofing - Bathroom',
    date: 'April 25, 2023',
    status: 'in-progress',
    vendor: 'WaterGuard Solutions',
    vendorPhone: '+60 13-456-7890',
    vendorRating: 4.5,
    amount: 'RM 650.00',
    details: {
      description: 'Bathroom waterproofing treatment to prevent leakage issues.',
      address: '123 Jalan Bukit Bintang, Kuala Lumpur',
      technician: 'Tan Wei Ming',
      scheduledDate: 'April 25-27, 2023',
      warranty: 'Will be valid for 1 year upon completion',
      notes: 'Water testing to be conducted after completion.'
    },
    statusHistory: [{
      status: 'Order Created',
      timestamp: 'April 24, 2023 11:00 AM'
    }, {
      status: 'Vendor Assigned',
      timestamp: 'April 24, 2023 11:20 AM',
      note: 'WaterGuard Solutions assigned based on location'
    }, {
      status: 'Vendor Confirmed',
      timestamp: 'April 24, 2023 02:00 PM'
    }, {
      status: 'Inspection Completed',
      timestamp: 'April 25, 2023 09:00 AM'
    }, {
      status: 'Technician Arrived',
      timestamp: 'April 25, 2023 10:00 AM'
    }, {
      status: 'Work In Progress',
      timestamp: 'April 25, 2023 10:30 AM'
    }]
  }, {
    id: 3,
    service: 'Door Hinge Replacement',
    date: 'May 5, 2023',
    status: 'pending-vendor',
    vendor: 'FixIt Pro',
    vendorPhone: '+60 14-567-8901',
    vendorRating: 4.9,
    amount: 'RM 120.00',
    details: {
      description: 'Replace 3 door hinges on master bedroom door.',
      address: '123 Jalan Bukit Bintang, Kuala Lumpur',
      technician: 'To be assigned',
      scheduledDate: 'Pending confirmation',
      warranty: 'Will be valid for 14 days upon completion',
      notes: 'Customer will provide preferred hinge style.'
    },
    statusHistory: [{
      status: 'Order Created',
      timestamp: 'May 4, 2023 03:00 PM'
    }, {
      status: 'Vendor Assigned',
      timestamp: 'May 4, 2023 03:15 PM',
      note: 'FixIt Pro assigned based on location'
    }]
  }];
  const toggleOrderExpansion = (orderId: number) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
    }
  };
  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'pending-vendor':
        return <Clock className="h-5 w-5 text-yellow-500" data-id="element-810" />;
      case 'accepted':
      case 'pre-inspect':
        return <Calendar className="h-5 w-5 text-blue-500" data-id="element-811" />;
      case 'inspection':
        return <ClipboardCheck className="h-5 w-5 text-purple-500" data-id="element-812" />;
      case 'arrived':
        return <Truck className="h-5 w-5 text-orange-500" data-id="element-813" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-blue-500" data-id="element-814" />;
      case 'pending-review':
        return <Star className="h-5 w-5 text-yellow-500" data-id="element-815" />;
      case 'completed':
      case 'closed':
        return <CheckCircle className="h-5 w-5 text-green-500" data-id="element-816" />;
      case 'rejected':
      case 'rework':
        return <XCircle className="h-5 w-5 text-red-500" data-id="element-817" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" data-id="element-818" />;
    }
  };
  const getStatusBadgeColor = (status: OrderStatus) => {
    switch (status) {
      case 'pending-vendor':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
      case 'pre-inspect':
        return 'bg-blue-100 text-blue-800';
      case 'inspection':
        return 'bg-purple-100 text-purple-800';
      case 'arrived':
        return 'bg-orange-100 text-orange-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending-review':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
      case 'closed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
      case 'rework':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusLabel = (status: OrderStatus) => {
    const labels: Record<OrderStatus, string> = {
      'pending-vendor': 'Pending Vendor Confirmation',
      accepted: 'Accepted',
      'pre-inspect': 'Pre-Inspection Scheduled',
      inspection: 'Inspection',
      arrived: 'Technician Arrived',
      'in-progress': 'In Progress',
      'pending-review': 'Pending Your Review',
      completed: 'Completed',
      closed: 'Closed',
      rejected: 'Rejected',
      rework: 'Rework Required'
    };
    return labels[status];
  };
  const handleReviewSubmit = () => {
    console.log('Review submitted:', {
      rating,
      reviewText,
      order: selectedOrder
    });
    // In a real app, submit to API
    setShowReviewModal(false);
    setShowFeedbackModal(true);
    setRating(0);
    setReviewText('');
  };
  const handleFeedbackSubmit = () => {
    console.log('Feedback submitted:', feedbackText);
    // In a real app, submit to API and generate voucher
    alert('Thank you for your feedback! A RM 50 voucher has been added to your account.');
    setShowFeedbackModal(false);
    setFeedbackText('');
  };
  const handleRejectOrder = () => {
    console.log('Order rejected:', {
      order: selectedOrder,
      reason: rejectionReason
    });
    // In a real app, submit to API
    alert('Your rejection has been submitted to admin for review.');
    setShowRejectModal(false);
    setRejectionReason('');
  };
  const handleAcceptVendor = (order: Order) => {
    console.log('Vendor accepted for order:', order.id);
    alert('Vendor confirmed! You will be notified when the inspection is scheduled.');
  };
  const handleDownloadInvoice = (order: Order) => {
    setInvoiceOrder(order);
    setShowInvoiceModal(true);
  };
  const handlePrintInvoice = () => {
    window.print();
  };
  const generateInvoiceNumber = (orderId: number) => {
    return `INV-2023-${String(orderId).padStart(6, '0')}`;
  };
  const filteredOrders = orders.filter(order => {
    if (filterStatus !== 'all' && order.status !== filterStatus) {
      return false;
    }
    if (searchQuery && !order.service.toLowerCase().includes(searchQuery.toLowerCase()) && !order.vendor.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  return <div data-id="element-819">
      <div className="flex items-center justify-between mb-6" data-id="element-820">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-821">My Orders</h1>
        <button className="flex items-center text-primary hover:text-primary/80" data-id="element-822">
          <Bell className="h-5 w-5 mr-2" data-id="element-823" />
          <span className="text-sm font-medium" data-id="element-824">Notifications (2)</span>
        </button>
      </div>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6" data-id="element-825">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-826">
          <div className="flex flex-wrap gap-2" data-id="element-827">
            <button onClick={() => setFilterStatus('all')} className={`px-3 py-2 text-sm rounded-md ${filterStatus === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-828">
              All
            </button>
            <button onClick={() => setFilterStatus('pending-vendor')} className={`px-3 py-2 text-sm rounded-md ${filterStatus === 'pending-vendor' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-829">
              Pending
            </button>
            <button onClick={() => setFilterStatus('in-progress')} className={`px-3 py-2 text-sm rounded-md ${filterStatus === 'in-progress' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-830">
              In Progress
            </button>
            <button onClick={() => setFilterStatus('pending-review')} className={`px-3 py-2 text-sm rounded-md ${filterStatus === 'pending-review' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-831">
              Needs Review
            </button>
            <button onClick={() => setFilterStatus('completed')} className={`px-3 py-2 text-sm rounded-md ${filterStatus === 'completed' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-832">
              Completed
            </button>
          </div>
          <div className="relative" data-id="element-833">
            <input type="text" placeholder="Search orders..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full md:w-64 focus:outline-none focus:ring-primary focus:border-primary" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-834" />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-835" />
          </div>
        </div>
      </div>
      {/* Orders List */}
      <div className="space-y-4" data-id="element-836">
        {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden" data-id="element-837">
              <div className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50" onClick={() => toggleOrderExpansion(order.id)} data-id="element-838">
                <div className="flex items-center space-x-3 flex-1" data-id="element-839">
                  {getStatusIcon(order.status)}
                  <div className="flex-1" data-id="element-840">
                    <h3 className="font-medium text-gray-900" data-id="element-841">
                      {order.service}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1" data-id="element-842">
                      <span data-id="element-843">{order.vendor}</span>
                      {order.vendorRating && <>
                          <span data-id="element-844">•</span>
                          <div className="flex items-center" data-id="element-845">
                            <Star className="h-4 w-4 text-yellow-500 fill-current mr-1" data-id="element-846" />
                            <span data-id="element-847">{order.vendorRating}</span>
                          </div>
                        </>}
                      <span data-id="element-848">•</span>
                      <span data-id="element-849">{order.date}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-850">
                  <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${getStatusBadgeColor(order.status)}`} data-id="element-851">
                    {getStatusLabel(order.status)}
                  </span>
                  <span className="font-medium" data-id="element-852">{order.amount}</span>
                  {expandedOrder === order.id ? <ChevronUp className="h-5 w-5 text-gray-400" data-id="element-853" /> : <ChevronDown className="h-5 w-5 text-gray-400" data-id="element-854" />}
                </div>
              </div>
              {expandedOrder === order.id && <div className="border-t border-gray-200" data-id="element-855">
                  {/* Vendor Actions */}
                  {order.status === 'pending-vendor' && <div className="p-4 bg-yellow-50 border-b border-yellow-100" data-id="element-856">
                      <div className="flex items-start justify-between" data-id="element-857">
                        <div className="flex items-start" data-id="element-858">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" data-id="element-859" />
                          <div data-id="element-860">
                            <p className="text-sm font-medium text-yellow-900" data-id="element-861">
                              Waiting for vendor confirmation
                            </p>
                            <p className="text-sm text-yellow-700 mt-1" data-id="element-862">
                              {order.vendor} has been assigned based on your
                              location. They will confirm within 24 hours.
                            </p>
                          </div>
                        </div>
                        <button onClick={() => {
                setSelectedOrder(order);
                setShowRejectModal(true);
              }} className="ml-4 px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50" data-id="element-863">
                          Reject Vendor
                        </button>
                      </div>
                    </div>}
                  {/* Review Alert */}
                  {order.canReview && order.status === 'completed' && <div className="p-4 bg-primary/5 border-b border-primary/10" data-id="element-864">
                      <div className="flex items-start justify-between" data-id="element-865">
                        <div className="flex items-start" data-id="element-866">
                          <Star className="h-5 w-5 text-primary mr-2 mt-0.5" data-id="element-867" />
                          <div data-id="element-868">
                            <p className="text-sm font-medium text-gray-900" data-id="element-869">
                              Review this service
                            </p>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-870">
                              Auto-approved in 3 days if no review. Deadline:{' '}
                              {order.reviewDeadline}
                            </p>
                          </div>
                        </div>
                        <button onClick={() => {
                setSelectedOrder(order);
                setShowReviewModal(true);
              }} className="ml-4 px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90" data-id="element-871">
                          Review Now
                        </button>
                      </div>
                    </div>}
                  <div className="p-6" data-id="element-872">
                    {/* Status Timeline */}
                    {order.statusHistory && <div className="mb-6" data-id="element-873">
                        <h4 className="font-medium text-gray-900 mb-4" data-id="element-874">
                          Order Timeline
                        </h4>
                        <div className="space-y-4" data-id="element-875">
                          {order.statusHistory.map((item, index) => <div key={index} className="flex" data-id="element-876">
                              <div className="flex flex-col items-center mr-4" data-id="element-877">
                                <div className="h-2 w-2 rounded-full bg-primary" data-id="element-878"></div>
                                {index < order.statusHistory!.length - 1 && <div className="h-full w-0.5 bg-gray-200 mt-1" data-id="element-879"></div>}
                              </div>
                              <div className="flex-1 pb-4" data-id="element-880">
                                <p className="text-sm font-medium text-gray-900" data-id="element-881">
                                  {item.status}
                                </p>
                                <p className="text-xs text-gray-500" data-id="element-882">
                                  {item.timestamp}
                                </p>
                                {item.note && <p className="text-xs text-gray-600 mt-1" data-id="element-883">
                                    {item.note}
                                  </p>}
                              </div>
                            </div>)}
                        </div>
                      </div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-884">
                      <div data-id="element-885">
                        <h4 className="font-medium text-gray-900 mb-3" data-id="element-886">
                          Service Details
                        </h4>
                        <p className="text-sm text-gray-700 mb-4" data-id="element-887">
                          {order.details.description}
                        </p>
                        <div className="space-y-2" data-id="element-888">
                          <div className="flex items-start" data-id="element-889">
                            <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-890" />
                            <span className="text-sm text-gray-700" data-id="element-891">
                              {order.details.address}
                            </span>
                          </div>
                          <div className="flex items-start" data-id="element-892">
                            <User className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-893" />
                            <span className="text-sm text-gray-700" data-id="element-894">
                              Technician: {order.details.technician}
                            </span>
                          </div>
                          <div className="flex items-start" data-id="element-895">
                            <Calendar className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-896" />
                            <span className="text-sm text-gray-700" data-id="element-897">
                              {order.status === 'completed' ? `Completed on: ${order.details.completedDate}` : `Scheduled: ${order.details.scheduledDate}`}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div data-id="element-898">
                        <h4 className="font-medium text-gray-900 mb-3" data-id="element-899">
                          Vendor Information
                        </h4>
                        <div className="space-y-3" data-id="element-900">
                          <div data-id="element-901">
                            <p className="text-sm font-medium text-gray-700" data-id="element-902">
                              {order.vendor}
                            </p>
                            {order.vendorRating && <div className="flex items-center mt-1" data-id="element-903">
                                <Star className="h-4 w-4 text-yellow-500 fill-current mr-1" data-id="element-904" />
                                <span className="text-sm text-gray-600" data-id="element-905">
                                  {order.vendorRating} / 5.0
                                </span>
                              </div>}
                          </div>
                          {order.vendorPhone && <div data-id="element-906">
                              <p className="text-sm font-medium text-gray-700" data-id="element-907">
                                Contact:
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-908">
                                {order.vendorPhone}
                              </p>
                            </div>}
                          <div data-id="element-909">
                            <p className="text-sm font-medium text-gray-700" data-id="element-910">
                              Warranty:
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-911">
                              {order.details.warranty}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center" data-id="element-912">
                      <div className="flex space-x-3" data-id="element-913">
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50" data-id="element-914">
                          Contact Support
                        </button>
                        {(order.status === 'completed' || order.status === 'closed') && <>
                            <button onClick={() => handleDownloadInvoice(order)} className="px-4 py-2 border border-primary text-primary text-sm rounded-md hover:bg-primary/5 flex items-center" data-id="element-915">
                              <FileText className="h-4 w-4 mr-2" data-id="element-916" />
                              View Invoice
                            </button>
                          </>}
                      </div>
                      {order.status === 'pending-vendor' && <button onClick={() => handleAcceptVendor(order)} className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90" data-id="element-917">
                          Accept Vendor
                        </button>}
                    </div>
                  </div>
                </div>}
            </div>) : <div className="bg-white rounded-lg shadow p-8 text-center" data-id="element-918">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100" data-id="element-919">
              <Clock className="h-6 w-6 text-gray-400" data-id="element-920" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-921">
              No orders found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-922">
              {searchQuery || filterStatus !== 'all' ? 'Try changing your search or filter criteria' : "You haven't placed any orders yet"}
            </p>
          </div>}
      </div>
      {/* Review Modal */}
      {showReviewModal && selectedOrder && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-923">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-924">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-925">
              Review Service
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-926">
              How was your experience with {selectedOrder.vendor}?
            </p>
            <div className="mb-4" data-id="element-927">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-928">
                Rating
              </label>
              <div className="flex space-x-2" data-id="element-929">
                {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setRating(star)} className="focus:outline-none" data-id="element-930">
                    <Star className={`h-8 w-8 ${star <= rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} data-id="element-931" />
                  </button>)}
              </div>
            </div>
            <div className="mb-4" data-id="element-932">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-933">
                Review (Optional)
              </label>
              <textarea rows={4} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Share your experience..." value={reviewText} onChange={e => setReviewText(e.target.value)} data-id="element-934"></textarea>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-935">
              <button onClick={() => setShowReviewModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-936">
                Cancel
              </button>
              <button onClick={handleReviewSubmit} disabled={rating === 0} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-937">
                Submit Review
              </button>
            </div>
          </div>
        </div>}
      {/* Feedback Modal */}
      {showFeedbackModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-938">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-939">
            <div className="flex items-center mb-4" data-id="element-940">
              <Gift className="h-6 w-6 text-primary mr-2" data-id="element-941" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-942">
                Get a Voucher!
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4" data-id="element-943">
              Share your feedback about our service and receive a RM 50 voucher
              for your next order!
            </p>
            <div className="mb-4" data-id="element-944">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-945">
                Your Feedback
              </label>
              <textarea rows={4} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Tell us how we can improve..." value={feedbackText} onChange={e => setFeedbackText(e.target.value)} data-id="element-946"></textarea>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-947">
              <button onClick={() => setShowFeedbackModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-948">
                Skip
              </button>
              <button onClick={handleFeedbackSubmit} disabled={!feedbackText.trim()} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-949">
                Submit & Get Voucher
              </button>
            </div>
          </div>
        </div>}
      {/* Reject Vendor Modal */}
      {showRejectModal && selectedOrder && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-950">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-951">
            <div className="flex items-center mb-4" data-id="element-952">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" data-id="element-953" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-954">
                Reject Vendor Assignment
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4" data-id="element-955">
              Please provide a reason for rejecting {selectedOrder.vendor}. Your
              request will be reviewed by our admin team.
            </p>
            <div className="mb-4" data-id="element-956">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-957">
                Reason for Rejection
              </label>
              <textarea rows={4} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Please explain why you want to reject this vendor..." value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} data-id="element-958"></textarea>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-959">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-960">
                Cancel
              </button>
              <button onClick={handleRejectOrder} disabled={!rejectionReason.trim()} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50" data-id="element-961">
                Submit Rejection
              </button>
            </div>
          </div>
        </div>}
      {/* Invoice Modal */}
      {showInvoiceModal && invoiceOrder && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-962">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8" data-id="element-963">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center print:hidden" data-id="element-964">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-965">Invoice</h3>
              <div className="flex items-center space-x-3" data-id="element-966">
                <button onClick={handlePrintInvoice} className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 flex items-center" data-id="element-967">
                  <Download className="h-4 w-4 mr-2" data-id="element-968" />
                  Download PDF
                </button>
                <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-969">
                  <span className="text-2xl" data-id="element-970">&times;</span>
                </button>
              </div>
            </div>
            <div className="p-8" data-id="element-971">
              {/* Invoice Header */}
              <div className="flex justify-between items-start mb-8" data-id="element-972">
                <div data-id="element-973">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-974">
                    INVOICE
                  </h1>
                  <p className="text-gray-600" data-id="element-975">Home Service Platform</p>
                  <p className="text-sm text-gray-500" data-id="element-976">123 Business Street</p>
                  <p className="text-sm text-gray-500" data-id="element-977">
                    Kuala Lumpur, Malaysia
                  </p>
                </div>
                <div className="text-right" data-id="element-978">
                  <p className="text-sm text-gray-600" data-id="element-979">Invoice Number</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-980">
                    {generateInvoiceNumber(invoiceOrder.id)}
                  </p>
                  <p className="text-sm text-gray-600 mt-2" data-id="element-981">Invoice Date</p>
                  <p className="text-sm text-gray-900" data-id="element-982">
                    {new Date(invoiceOrder.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {/* Bill To / Service Provider */}
              <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-983">
                <div data-id="element-984">
                  <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-985">
                    BILL TO
                  </h3>
                  <p className="text-gray-900" data-id="element-986">{invoiceOrder.customerName}</p>
                  <p className="text-sm text-gray-600" data-id="element-987">
                    {invoiceOrder.customerAddress}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-988">
                    {invoiceOrder.customerPhone}
                  </p>
                </div>
                <div data-id="element-989">
                  <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-990">
                    SERVICE PROVIDED BY
                  </h3>
                  <p className="text-gray-900" data-id="element-991">{invoiceOrder.vendor}</p>
                  {invoiceOrder.vendorPhone && <p className="text-sm text-gray-600" data-id="element-992">
                      {invoiceOrder.vendorPhone}
                    </p>}
                </div>
              </div>
              {/* Service Details */}
              <div className="mb-8" data-id="element-993">
                <table className="w-full" data-id="element-994">
                  <thead className="bg-gray-50 border-b-2 border-gray-200" data-id="element-995">
                    <tr data-id="element-996">
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-900" data-id="element-997">
                        Service Description
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-900" data-id="element-998">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-900" data-id="element-999">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1000">
                    <tr className="border-b border-gray-200" data-id="element-1001">
                      <td className="px-4 py-4 text-sm text-gray-900" data-id="element-1002">
                        {invoiceOrder.serviceName}
                        {invoiceOrder.details?.description && <p className="text-xs text-gray-500 mt-1" data-id="element-1003">
                            {invoiceOrder.details.description}
                          </p>}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 text-right" data-id="element-1004">
                        {invoiceOrder.completedAt ? new Date(invoiceOrder.completedAt).toLocaleDateString() : invoiceOrder.date}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-1005">
                        MYR {invoiceOrder.servicePrice.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Total */}
              <div className="flex justify-end mb-8" data-id="element-1006">
                <div className="w-64" data-id="element-1007">
                  <div className="flex justify-between py-2 border-t border-gray-200" data-id="element-1008">
                    <span className="text-sm text-gray-600" data-id="element-1009">Subtotal:</span>
                    <span className="text-sm text-gray-900" data-id="element-1010">
                      MYR {invoiceOrder.servicePrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-t-2 border-gray-900" data-id="element-1011">
                    <span className="text-base font-bold text-gray-900" data-id="element-1012">
                      Total:
                    </span>
                    <span className="text-base font-bold text-gray-900" data-id="element-1013">
                      MYR {invoiceOrder.servicePrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              {/* Payment Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-8" data-id="element-1014">
                <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1015">
                  PAYMENT INFORMATION
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1016">Status: Paid</p>
                <p className="text-sm text-gray-600" data-id="element-1017">
                  Payment Method:{' '}
                  {invoiceOrder.details?.paymentMethod || 'Credit Card'}
                </p>
                <p className="text-sm text-gray-600" data-id="element-1018">
                  Payment Date:{' '}
                  {new Date(invoiceOrder.createdAt).toLocaleDateString()}
                </p>
              </div>
              {/* Warranty Info */}
              {invoiceOrder.details?.warranty && <div className="border-t border-gray-200 pt-4" data-id="element-1019">
                  <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1020">
                    WARRANTY
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1021">
                    {invoiceOrder.details.warranty}
                  </p>
                </div>}
              {/* Footer */}
              <div className="mt-8 pt-8 border-t border-gray-200 text-center text-sm text-gray-500" data-id="element-1022">
                <p data-id="element-1023">Thank you for your business!</p>
                <p className="mt-2" data-id="element-1024">
                  For any questions regarding this invoice, please contact our
                  support team.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}